import logo from "@/assets/brim-mob-logo.png";
import { Link } from "react-router-dom";
import { SHOPIFY_STORE_PERMANENT_DOMAIN } from "@/lib/shopify";
import { NewsletterForm } from "@/components/NewsletterForm";
import { Instagram, Facebook, Youtube } from "lucide-react";

type FooterLink = { label: string; to?: string; href?: string };

const columns: { h: string; l: FooterLink[] }[] = [
  {
    h: "SHOP",
    l: [
      { label: "Drops", to: "/drops" },
      { label: "Hats", to: "/hats" },
      { label: "League", to: "/league" },
      { label: "Exclusives", to: "/exclusives" },
      { label: "Accessories", to: "/accessories" },
      { label: "Sale", to: "/sale" },
    ],
  },
  {
    h: "ACCOUNT",
    l: [
      { label: "Sign In", to: "/account/sign-in" },
      { label: "My Account", to: "/account/profile" },
      { label: "Order Status", to: "/account/order-status" },
      { label: "Forgot Password", to: "/account/forgot-password" },
    ],
  },
  {
    h: "HELP",
    l: [
      { label: "Support", to: "/help/support" },
      { label: "Shipping", to: "/help/shipping" },
      { label: "Returns", to: "/help/returns" },
      { label: "Privacy", to: "/help/privacy" },
      { label: "Terms", to: "/help/terms" },
    ],
  },
];

const TikTokIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.95a8.16 8.16 0 0 0 4.77 1.52V7.07a4.85 4.85 0 0 1-1.84-.38Z" />
  </svg>
);

const socials = [
  { label: "Instagram", href: "https://instagram.com/thebrimmob", Icon: Instagram },
  { label: "TikTok", href: "https://tiktok.com/@thebrimmob", Icon: TikTokIcon },
  { label: "Facebook", href: "https://facebook.com/thebrimmob", Icon: Facebook },
  { label: "YouTube", href: "https://youtube.com/@thebrimmob", Icon: Youtube },
];

export const SiteFooter = () => (
  <>
    <section className="py-24 bg-foreground text-background">
      <div className="container max-w-2xl text-center">
        <img src={logo} alt="Brim Mob" className="h-32 md:h-40 w-auto mx-auto mb-8 invert" />
        <h2 className="font-display text-4xl md:text-5xl mb-4">JOIN THE MOB</h2>
        <p className="text-background/70 mb-8">Sign up and get <span className="text-accent font-display">15% OFF</span> your first order. Plus early access to drops, member-only releases, and pre-sale prices before they hit the public.</p>
        <NewsletterForm source="footer" />
      </div>
    </section>

    <footer className="border-t border-border py-12 bg-background">
      <div className="container grid grid-cols-2 md:grid-cols-5 gap-8 mb-12">
        {columns.map((c) => (
          <div key={c.h}>
              <h2 className="font-display text-lg mb-4">{c.h}</h2>
            <ul className="space-y-2 text-sm text-muted-foreground">
              {c.l.map((i) => (
                <li key={i.label}>
                  {i.to ? (
                    <Link to={i.to} className="hover:text-accent transition-colors">{i.label}</Link>
                  ) : (
                    <a href={i.href} target="_blank" rel="noopener noreferrer" className="hover:text-accent transition-colors">{i.label}</a>
                  )}
                </li>
              ))}
            </ul>
          </div>
        ))}
        <div>
          <h2 className="font-display text-lg mb-4">SOCIALS</h2>
          <div className="flex items-center gap-4">
            {socials.map(({ label, href, Icon }) => (
              <a
                key={label}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={label}
                className="text-muted-foreground hover:text-accent transition-colors"
              >
                <Icon className="w-5 h-5" />
              </a>
            ))}
          </div>
        </div>
      </div>
      <div className="container flex flex-col md:flex-row items-center justify-between border-t border-border pt-8 text-muted-foreground font-display">
        <p style={{ fontSize: "14.5px" }}>© 2026 BRIM MOB. ALL RIGHTS RESERVED.</p>
        <p className="mt-2 md:mt-0" style={{ fontSize: "14.5px" }}>FITTED FOR THE CITY.</p>
      </div>
    </footer>
  </>
);